import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSave, FiCheck, FiBriefcase, FiAlertCircle } = FiIcons;

const BankSettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    accountHolder: '',
    bankName: '',
    iban: '',
    swift: '',
    instructions: ''
  });

  // Load saved settings from localStorage on mount (Simulating backend fetch)
  useEffect(() => {
    const saved = localStorage.getItem('venue_bank_settings');
    if (saved) {
      setBankDetails(JSON.parse(saved));
    }
  }, []);

  const handleChange = (e) => {
    setBankDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setSuccess(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      localStorage.setItem('venue_bank_settings', JSON.stringify(bankDetails));
      setLoading(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }, 800);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bank Settings</h1>
          <p className="text-gray-600 mt-1">Configure your bank account details to accept direct transfers from guests.</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <SafeIcon icon={FiBriefcase} className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Bank Transfer Configuration</h2>
            </div>
            {success && (
              <span className="text-green-600 text-sm font-medium flex items-center animate-fade-in">
                <SafeIcon icon={FiCheck} className="mr-1 h-4 w-4" /> Saved Successfully
              </span>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3 text-blue-800 text-sm mb-6">
              <SafeIcon icon={FiAlertCircle} className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                These details will be displayed to guests who choose "Bank Transfer" as their payment method. 
                Ensure all information is accurate to avoid payment delays.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name</label>
                <input 
                  type="text" 
                  name="accountHolder" 
                  required 
                  value={bankDetails.accountHolder} 
                  onChange={handleChange}
                  placeholder="e.g. Venue Management Ltd." 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <input 
                  type="text" 
                  name="bankName" 
                  required 
                  value={bankDetails.bankName} 
                  onChange={handleChange}
                  placeholder="e.g. Chase Bank" 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SWIFT / BIC Code</label>
                <input 
                  type="text" 
                  name="swift" 
                  required 
                  value={bankDetails.swift} 
                  onChange={handleChange}
                  placeholder="CHASUS33XXX" 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">IBAN / Account Number</label>
                <input 
                  type="text" 
                  name="iban" 
                  required 
                  value={bankDetails.iban} 
                  onChange={handleChange}
                  placeholder="US12 3456 7890 1234 5678" 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Instructions (Optional)</label>
                <textarea 
                  name="instructions" 
                  rows="3" 
                  value={bankDetails.instructions} 
                  onChange={handleChange}
                  placeholder="e.g. Please include your Reservation ID in the transfer description." 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                ></textarea>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-100 flex justify-end">
              <button 
                type="submit" 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors flex items-center shadow-sm disabled:opacity-70"
              >
                {loading ? 'Saving...' : (
                  <>
                    <SafeIcon icon={FiSave} className="mr-2 h-4 w-4" /> Save Bank Details
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BankSettingsPage;